/**
 * Country and Shipping Utilities Module
 * Handles country recognition, ISO code conversion, and shipping method selection
 * Extracted from csvImportApiClient.js for better maintainability
 */

import mockDataService from '../mockDataService';

// Environment configuration
const USE_MOCK_DATA = process.env.REACT_APP_API_MODE === 'mock';
const DEBUG_API = process.env.REACT_APP_DEBUG_API === 'true';

class CountryShippingUtils {
    constructor(baseApiClient) {
        this.baseClient = baseApiClient;
        this.api = baseApiClient.api; // Keep for backward compatibility
        this._taxRuleCache = null; // Cache for API-loaded tax rules per country ISO
    }

    /**
     * Load tax rules from Shopware API and build country → taxRate map
     * Uses the standard tax (19% DE) and its tax_rule entries for other countries
     * @returns {Object} Map of ISO code → { taxRate, taxId }
     */
    async loadTaxRulesFromApi() {
        if (this._taxRuleCache) return this._taxRuleCache;

        try {
            // Find the standard tax rate (19% DE)
            const taxResp = await this.api.post('/search/tax', {
                filter: [{ type: 'equals', field: 'taxRate', value: 19 }],
                limit: 1
            });

            const standardTax = taxResp.data?.data?.[0];
            if (!standardTax) {
                if (DEBUG_API) console.warn('[Country Utils] Standard tax (19%) not found in API');
                return null;
            }

            // Load all tax rules for this tax with country associations
            const rulesResp = await this.api.post('/search/tax-rule', {
                filter: [{ type: 'equals', field: 'taxId', value: standardTax.id }],
                associations: { country: {} },
                limit: 100
            });

            const rules = rulesResp.data?.data || [];
            const cache = {};

            // Default: DE uses the standard tax rate directly
            cache['DE'] = { taxRate: standardTax.taxRate, taxId: standardTax.id };

            for (const rule of rules) {
                const iso = rule.country?.iso;
                if (iso && rule.taxRate !== undefined) {
                    cache[iso] = { taxRate: rule.taxRate, taxId: standardTax.id };
                }
            }

            if (DEBUG_API) {
                console.log(`[Country Utils] Loaded ${Object.keys(cache).length} tax rules from API:`,
                    Object.entries(cache).map(([iso, c]) => `${iso}=${c.taxRate}%`).join(', '));
            }

            this._taxRuleCache = cache;
            return cache;
        } catch (error) {
            console.warn('[Country Utils] Failed to load tax rules from API, using static fallback:', error.message);
            return null;
        }
    }

    /**
     * Convert country name to ISO code for proper matching
     * @param {string} countryName - Country name from CSV
     * @returns {string} ISO code (e.g., "DE", "AT")
     */
    getCountryIsoCode(countryName) {
        if (!countryName) return 'DE';
        
        const country = countryName.toLowerCase().trim();
        
        // Direct ISO code mapping
        const countryMappings = {
            // German names
            'deutschland': 'DE',
            'österreich': 'AT',
            'schweiz': 'CH',
            'frankreich': 'FR',
            'belgien': 'BE',
            'niederlande': 'NL',
            'italien': 'IT',
            'spanien': 'ES',
            'polen': 'PL',
            'tschechien': 'CZ',
            'ungarn': 'HU',
            'slowakei': 'SK',
            
            // English names
            'germany': 'DE',
            'austria': 'AT',
            'switzerland': 'CH',
            'france': 'FR',
            'belgium': 'BE',
            'netherlands': 'NL',
            'italy': 'IT',
            'spain': 'ES',
            'poland': 'PL',
            'czech republic': 'CZ',
            'hungary': 'HU',
            'slovakia': 'SK',
            
            // Already ISO codes (pass through)
            'de': 'DE',
            'at': 'AT',
            'ch': 'CH',
            'fr': 'FR',
            'be': 'BE',
            'nl': 'NL',
            'it': 'IT',
            'es': 'ES',
            'pl': 'PL',
            'cz': 'CZ',
            'hu': 'HU',
            'sk': 'SK'
        };
        
        const isoCode = countryMappings[country];
        
        if (DEBUG_API) {
            console.log(`[Country Utils] Country name conversion: "${countryName}" -> "${isoCode || 'DE (fallback)'}"`);
        }
        
        return isoCode || 'DE'; // Default to Germany if not found
    }

    /**
     * Get country ID by name for address creation
     * @param {string} countryName - Country name
     * @returns {string|null} Country ID or null if not found
     */
    async getCountryIdByName(countryName) {
        if (!countryName) return null;

        try {
            // Convert to ISO code first
            const isoCode = this.getCountryIsoCode(countryName);
            
            // Search for country by ISO code
            const response = await this.api.post('/search/country', {
                filter: [
                    { type: 'equals', field: 'iso', value: isoCode }
                ],
                limit: 1
            });

            if (response.data?.data?.length > 0) {
                const country = response.data.data[0];
                if (DEBUG_API) {
                    console.log(`[Country Utils] Found country ID for ${countryName} (${isoCode}): ${country.id}`);
                }
                return country.id;
            }

            // Fallback: search by name
            const nameResponse = await this.api.post('/search/country', {
                filter: [
                    { type: 'contains', field: 'name', value: countryName }
                ],
                limit: 1
            });

            if (nameResponse.data?.data?.length > 0) {
                const country = nameResponse.data.data[0];
                if (DEBUG_API) {
                    console.log(`[Country Utils] Found country ID by name for ${countryName}: ${country.id}`);
                }
                return country.id;
            }

            console.warn(`[Country Utils] Country not found: ${countryName}`);
            return null;

        } catch (error) {
            console.error(`[Country Utils] Error finding country ${countryName}:`, error);
            return null;
        }
    }

    /**
     * Extract country from CSV row using profile field mapping
     * @param {Object} row - CSV row data
     * @param {Object} profile - Profile configuration
     * @returns {string} Country name or 'DE' as fallback
     */
    extractCountryFromRow(row, profile) {
        let country = 'DE'; // Default fallback
        
        // Try to get country from CSV using profile field mapping
        if (profile.fieldMapping) {
            const countryMappingKey = Object.keys(profile.fieldMapping).find(key => 
                profile.fieldMapping[key].includes('country') || 
                key.toLowerCase().includes('country') ||
                key.toLowerCase().includes('land')
            );
            if (countryMappingKey && row[countryMappingKey]) {
                country = row[countryMappingKey];
            }
        }
        
        // Fallback to direct 'Country' column if no mapping found
        if (country === 'DE' && row['Country']) {
            country = row['Country'];
        }
        
        return country;
    }

    /**
     * Find country-specific shipping method in profile (synchronous)
     * @param {string} countryName - Original country name
     * @param {string} countryIsoCode - ISO code
     * @param {Array} countryShippingMethods - Profile shipping methods
     * @returns {Object|null} Shipping method config or null
     */
    findCountrySpecificShippingMethodSync(countryName, countryIsoCode, countryShippingMethods) {
        if (!countryShippingMethods || countryShippingMethods.length === 0) {
            if (DEBUG_API) {
                console.log(`[Country Utils] No country shipping methods in profile`);
            }
            return null;
        }

        if (DEBUG_API) {
            console.log(`[Country Utils] Looking for country: "${countryName}" (ISO: ${countryIsoCode})`);
            console.log(`[Country Utils] Available profile shipping methods:`, countryShippingMethods);
        }

        // Get available countries from cache or mockData
        let countries = [];
        if (USE_MOCK_DATA) {
            countries = mockDataService.countries || [];
        } else {
            // Use realistic Shopware 6 country IDs for production
            countries = [
                { id: '018ad12f185c7185851e2fdd7aa8206d', name: 'Deutschland', iso: 'DE' },
                { id: '018ad12f185c7185851e2fdd7aa8206e', name: 'Österreich', iso: 'AT' },  
                { id: '018ad12f185c7185851e2fdd7aa8206f', name: 'Belgien', iso: 'BE' },
                { id: '018ad12f185c7185851e2fdd7aa82070', name: 'Schweiz', iso: 'CH' },
                { id: '018ad12f185c7185851e2fdd7aa82071', name: 'Frankreich', iso: 'FR' },
                { id: '018ad12f185c7185851e2fdd7aa82072', name: 'Niederlande', iso: 'NL' },
                { id: '018ad12f185c7185851e2fdd7aa82073', name: 'Italien', iso: 'IT' },
                { id: '018ad12f185c7185851e2fdd7aa82074', name: 'Spanien', iso: 'ES' },
                { id: '018ad12f185c7185851e2fdd7aa82075', name: 'Polen', iso: 'PL' },
                { id: '018ad12f185c7185851e2fdd7aa82076', name: 'Tschechien', iso: 'CZ' }
            ];
        }

        // Try to find matching country by multiple criteria
        const foundCountry = countries.find(c => {
            // Match by exact name (case insensitive)
            if (c.name.toLowerCase() === countryName.toLowerCase()) {
                if (DEBUG_API) {
                    console.log(`[Country Utils] Found country by name: ${c.name} (${c.iso}) -> ${c.id}`);
                }
                return true;
            }
            
            // Match by ISO code
            if (c.iso === countryIsoCode) {
                if (DEBUG_API) {
                    console.log(`[Country Utils] Found country by ISO: ${c.iso} -> ${c.id}`);
                }
                return true;
            }

            // Match by partial name (for variations)
            const countryNameLower = countryName.toLowerCase();
            const countryShortName = c.name.toLowerCase();
            if (countryNameLower.includes(countryShortName) || countryShortName.includes(countryNameLower)) {
                if (DEBUG_API) {
                    console.log(`[Country Utils] Found country by partial match: ${c.name} matches ${countryName} -> ${c.id}`);
                }
                return true;
            }

            return false;
        });

        if (foundCountry) {
            // Look for shipping method configuration for this country
            const shippingConfig = countryShippingMethods.find(csm => {
                const matches = csm.countryId === foundCountry.id;
                if (DEBUG_API) {
                    console.log(`[Country Utils] Checking profile shipping config: ${csm.countryId} === ${foundCountry.id} ? ${matches}`);
                }
                return matches;
            });

            if (shippingConfig) {
                if (DEBUG_API) {
                    console.log(`[Country Utils] ✅ Found country-specific shipping for ${countryName}: ${shippingConfig.shippingMethodId}`);
                }
                
                return {
                    countryId: foundCountry.id,
                    shippingMethodId: shippingConfig.shippingMethodId,
                    countryName: foundCountry.name,
                    countryIso: foundCountry.iso
                };
            } else {
                if (DEBUG_API) {
                    console.log(`[Country Utils] ❌ Country found but no shipping config: ${foundCountry.name} (${foundCountry.id})`);
                }
            }
        } else {
            if (DEBUG_API) {
                console.log(`[Country Utils] ❌ Country not found in lookup table: ${countryName} (${countryIsoCode})`);
            }
        }

        return null;
    }

    /**
     * Get country-specific tax and shipping configuration
     * @param {string} country - Country name or ISO code
     * @param {Object} profile - Profile configuration
     * @returns {Object} Tax and shipping configuration
     */
    async getCountryTaxShipping(country, profile) {
        // Static fallback configurations (used when API is unavailable)
        const staticFallback = {
            DE: { taxRate: 19, taxFree: 'net' },
            AT: { taxRate: 20, taxFree: 'tax-free' },
            FR: { taxRate: 20, taxFree: 'tax-free' },
            BE: { taxRate: 21, taxFree: 'tax-free' },
            NL: { taxRate: 21, taxFree: 'tax-free' }
        };

        // Convert country name to ISO code for matching
        const countryIsoCode = this.getCountryIsoCode(country);

        if (DEBUG_API) {
            console.log(`[Country Utils] Country matching: "${country}" -> "${countryIsoCode}"`);
        }

        // Try to get tax rate from Shopware API (cached after first call)
        let taxRate = null;
        let taxId = '018ad12f18c072b09b5565d801f600bf'; // default tax ID
        if (!USE_MOCK_DATA) {
            const apiTaxRules = await this.loadTaxRulesFromApi();
            if (apiTaxRules && apiTaxRules[countryIsoCode]) {
                taxRate = apiTaxRules[countryIsoCode].taxRate;
                taxId = apiTaxRules[countryIsoCode].taxId;
                if (DEBUG_API) {
                    console.log(`[Country Utils] Tax rate from API for ${countryIsoCode}: ${taxRate}%`);
                }
            }
        }

        // Fallback to static config if API didn't return a result
        if (taxRate === null) {
            const fallback = staticFallback[countryIsoCode] || staticFallback.DE;
            taxRate = fallback.taxRate;
            if (DEBUG_API) {
                console.log(`[Country Utils] Tax rate from static fallback for ${countryIsoCode}: ${taxRate}%`);
            }
        }

        const taxFree = countryIsoCode === 'DE' ? 'net' : 'tax-free';
        const baseConfig = {
            taxId,
            taxRate,
            taxFree,
            shippingMethodId: '018ad1acd4fa726bb25aa7e7aad99814' // default DE shipping
        };
        
        // PRIORITY 1: Check if profile has country-specific shipping methods configured (highest priority)
        if (profile.countryShippingMethods && profile.countryShippingMethods.length > 0) {
            console.log(`[Country Utils] 🔍 Profile has ${profile.countryShippingMethods.length} country shipping methods:`, profile.countryShippingMethods);
            console.log(`[Country Utils] 🔍 Looking for country: "${country}" (original), ISO: "${countryIsoCode}"`);
            
            // Try to find country-specific shipping method in profile
            // First, try to get the real country ID from the country name
            const countryConfig = await this.findCountrySpecificShippingMethodAsync(country, countryIsoCode, profile.countryShippingMethods);
            
            if (countryConfig) {
                console.log(`[Country Utils] ✅ Found profile country-specific shipping method for ${country} (${countryIsoCode}): ${countryConfig.shippingMethodId}`);
                return {
                    ...baseConfig,
                    shippingMethodId: countryConfig.shippingMethodId
                };
            } else {
                console.log(`[Country Utils] ⚠️ No profile country-specific shipping method found for ${country} (${countryIsoCode})`);
            }
        }
        
        // PRIORITY 2: Use profile default shipping method if configured (fallback for countries not specifically configured)
        if (profile.defaultValues?.shippingMethodId) {
            if (DEBUG_API) {
                console.log(`[Country Utils] ✅ Using profile default shipping method: ${profile.defaultValues.shippingMethodId} (no country-specific found)`);
            }
            return {
                ...baseConfig,
                shippingMethodId: profile.defaultValues.shippingMethodId
            };
        }
        
        // PRIORITY 3: Use hardcoded country defaults as last fallback
        if (DEBUG_API) {
            console.log(`[Country Utils] Using hardcoded country default for ${country}: ${baseConfig.shippingMethodId}`);
        }
        return baseConfig;
    }

    /**
     * Find country-specific shipping method in profile (asynchronous - with real country lookup)
     * @param {string} countryName - Original country name
     * @param {string} countryIsoCode - ISO code
     * @param {Array} countryShippingMethods - Profile shipping methods
     * @returns {Object|null} Shipping method config or null
     */
    async findCountrySpecificShippingMethodAsync(countryName, countryIsoCode, countryShippingMethods) {
        if (!countryShippingMethods || countryShippingMethods.length === 0) {
            if (DEBUG_API) {
                console.log(`[Country Utils] No country shipping methods in profile`);
            }
            return null;
        }

        if (DEBUG_API) {
            console.log(`[Country Utils] Looking for country: "${countryName}" (ISO: ${countryIsoCode})`);
            console.log(`[Country Utils] Available profile shipping methods:`, countryShippingMethods);
        }

        try {
            // Try to get the real country ID from Shopware API
            const countryId = await this.getCountryIdByName(countryName);
            
            if (countryId) {
                // Look for shipping method configuration for this country ID
                const shippingConfig = countryShippingMethods.find(csm => {
                    const matches = csm.countryId === countryId;
                    if (DEBUG_API) {
                        console.log(`[Country Utils] Checking profile shipping config: ${csm.countryId} === ${countryId} ? ${matches}`);
                    }
                    return matches;
                });

                if (shippingConfig) {
                    if (DEBUG_API) {
                        console.log(`[Country Utils] ✅ Found country-specific shipping for ${countryName}: ${shippingConfig.shippingMethodId}`);
                    }
                    
                    return {
                        countryId: countryId,
                        shippingMethodId: shippingConfig.shippingMethodId,
                        countryName: countryName,
                        countryIso: countryIsoCode
                    };
                } else {
                    if (DEBUG_API) {
                        console.log(`[Country Utils] ❌ Country found but no shipping config: ${countryName} (${countryId})`);
                    }
                }
            } else {
                if (DEBUG_API) {
                    console.log(`[Country Utils] ❌ Could not find country ID for: ${countryName}`);
                }
            }
        } catch (error) {
            console.error(`[Country Utils] Error looking up country ${countryName}:`, error);
        }

        // Fallback to sync method if async lookup fails
        return this.findCountrySpecificShippingMethodSync(countryName, countryIsoCode, countryShippingMethods);
    }

    /**
     * Determine shipping method based on shipping address and profile
     * @param {Object} shippingAddress - Shipping address object
     * @param {Object} profile - Profile configuration
     * @returns {Object} Shipping configuration
     */
    async getShippingConfiguration(shippingAddress, profile) {
        let shippingMethodId = profile.defaultValues?.shippingMethodId;
        
        // Determine country-specific shipping method based on shipping address
        if (shippingAddress?.countryId) {
            try {
                // Get country code from shipping address country ID
                const countryResponse = await this.api.get(`/country/${shippingAddress.countryId}`);
                if (countryResponse.data?.data?.iso) {
                    const countryCode = countryResponse.data.data.iso;
                    const countryConfig = await this.getCountryTaxShipping(countryCode, profile);
                    
                    if (countryConfig.shippingMethodId && countryConfig.shippingMethodId !== profile.defaultValues?.shippingMethodId) {
                        const oldShippingMethodId = shippingMethodId;
                        shippingMethodId = countryConfig.shippingMethodId;
                        if (DEBUG_API) {
                            console.log(`[Country Utils] ✅ Changed shipping method for ${countryCode}:`);
                            console.log(`[Country Utils]   From: ${oldShippingMethodId} (default)`);
                            console.log(`[Country Utils]   To: ${shippingMethodId} (country-specific)`);
                        }
                    } else {
                        if (DEBUG_API) {
                            console.log(`[Country Utils] ℹ️ Using default shipping method for ${countryCode}: ${shippingMethodId}`);
                        }
                    }
                }
            } catch (error) {
                console.warn(`[Country Utils] Could not determine country-specific shipping method:`, error);
            }
        }
        
        return {
            shippingMethodId: shippingMethodId || '018ad1acd4fa726bb25aa7e7aad99814' // Default fallback
        };
    }

    /**
     * Extract shipping address from CSV row
     * @param {Object} row - CSV row data
     * @param {Array} headers - CSV headers
     * @param {Object} profile - Profile configuration
     * @returns {Object} Shipping address object
     */
    async extractShippingAddressFromRow(row, headers, profile) {
        const shippingAddress = {
            id: this.generateUUID().replace(/-/g, ''),
            salutationId: '018ad12f185a73c581f6512df71d823e', // Default salutation
            company: '',
            department: '',
            firstName: '',
            lastName: '',
            additionalAddressLine1: '',
            additionalAddressLine2: '',
            street: '',
            zipcode: '',
            city: '',
            countryId: null,
            phoneNumber: ''
        };

        // Use profile field mapping to extract address data
        if (profile.fieldMapping) {
            for (const [csvField, shopwareField] of Object.entries(profile.fieldMapping)) {
                if (row[csvField] && shopwareField.startsWith('deliveries.shippingOrderAddress.')) {
                    const addressField = shopwareField.replace('deliveries.shippingOrderAddress.', '');
                    
                    if (addressField === 'country.name') {
                        // Special handling for country - convert name to ID
                        const countryName = row[csvField];
                        const countryId = await this.getCountryIdByName(countryName);
                        if (countryId) {
                            shippingAddress.countryId = countryId;
                        }
                    } else if (shippingAddress.hasOwnProperty(addressField)) {
                        shippingAddress[addressField] = row[csvField];
                    }
                }
            }
        }

        // Set default country if none found
        if (!shippingAddress.countryId) {
            // Default to Germany
            const defaultCountryId = await this.getCountryIdByName('Deutschland');
            shippingAddress.countryId = defaultCountryId || '018ad12f185c7185851e2fdd7aa8206d';
        }

        return shippingAddress;
    }

    /**
     * Generate UUID for addresses
     * @returns {string} UUID string
     */
    generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : ((r & 0x3) | 0x8);
            return v.toString(16);
        });
    }
}

export default CountryShippingUtils;